/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// RN + NativeWind mirror of `apps/web/src/features/chat/NewChatModal.tsx`.
// Presentation-only — all the orchestration (searchUsers, requestChat,
// MLS group creation, profile-intro send) lives in the host. The
// shared component exposes:
//   - `onSearch(query, signal)` — debounced search; host owns the
//     network call.
//   - `onPick(user)` — host runs `requestChat` + MLS + ensureConversation
//     and resolves with the conversationId.
//
// Naming: `NewChatModal` instead of `NewChatModal` to avoid
// colliding with the legacy `ComposeChatScreen` export and any
// in-flight uses of `NewChatModal` already in the workspace.

import { Copy, Search, X } from "lucide-react-native";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	Modal,
	Pressable,
	ScrollView,
	StyleSheet,
	Text,
	TextInput,
	View,
} from "react-native";
import { Avatar } from "../components/Avatar";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { WEB_INPUT_OUTLINE_RESET } from "../foundations/webInputStyle";
import { getCurrentUserAccountId } from "../sync/registerCurrentUser";

export interface NewChatModalDirectoryUser {
	readonly id: string;
	readonly username: string;
	readonly displayName: string;
	readonly role?: string | undefined;
	readonly company?: string | undefined;
	readonly avatarUri?: string | undefined;
	readonly presence?: "online" | "away" | "busy" | "offline" | undefined;
	/** 3PID contact confirmation (OTP'd email/phone). NOT the verification
	 *  seal — that is `identityVerified` (KYC/KYB). Kept for callers that need
	 *  the contact signal; does NOT render a badge. */
	readonly verified?: boolean | undefined;
	/** KYC/KYB identity verification. This is the ONLY signal that renders the
	 *  verification seal (Figma iconsax-verify). */
	readonly identityVerified?: boolean | undefined;
	/** Source bucket for the section header — directory ("ON PING") vs a
	 *  matched device/contact ("YOUR CONTACTS"). Defaults to "on-ping". */
	readonly source?: "contact" | "on-ping" | undefined;
}

export interface NewChatModalProps {
	readonly open: boolean;
	readonly onDismiss: () => void;
	// Host-driven search. Returns the directory matches for the query.
	// `signal` lets the host cancel stale requests when the user types
	// faster than the BE responds.
	readonly onSearch: (
		query: string,
		signal: AbortSignal,
	) => Promise<readonly NewChatModalDirectoryUser[]>;
	// Host picks the user; runs `requestChat` + MLS orchestration and
	// resolves with the local conversationId. Rejecting surfaces an
	// inline error.
	readonly onPick: (user: NewChatModalDirectoryUser) => Promise<string>;
	// Fired after a successful pick. The host can route into the chat
	// room from here.
	readonly onConversationStarted: (conversationId: string) => void;
	/** The user's shareable link, e.g. "ping.app/alessia.harrell". When
	 *  set, the "Share your link" box (Figma 4036:14120) renders in the
	 *  empty-query state with a copy affordance. */
	readonly shareLink?: string | undefined;
	/** The current user's own account id, excluded from results (you can't start
	 *  a chat with yourself). Defaults to the registered current-user account id,
	 *  so hosts don't need to pass it; provide it to override (e.g. in tests). */
	readonly selfUserId?: string | undefined;
}

export function NewChatModal(props: NewChatModalProps): JSX.Element | null {
	const { open, onDismiss, onSearch, onPick, onConversationStarted, shareLink, selfUserId } =
		props;
	const [query, setQuery] = useState("");
	const [results, setResults] = useState<
		readonly NewChatModalDirectoryUser[]
	>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [requestingId, setRequestingId] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const abortRef = useRef<AbortController | null>(null);

	// Never offer the current user as someone to message — you can't start a chat
	// with yourself. Filter their own account out of every result set (the id is
	// the directory account id, same space as the registered current-user id).
	const selfId = selfUserId ?? getCurrentUserAccountId();
	const visibleResults = useMemo(
		() => (selfId ? results.filter((u) => u.id !== selfId) : results),
		[results, selfId],
	);

	// Split results into the Figma sections: "YOUR CONTACTS" (matched
	// device contacts) and "ON PING" (directory). Device contacts aren't
	// available on desktop web yet, so in practice everything lands under
	// "ON PING" — but the split is honoured when the host tags a row.
	const sections = useMemo(() => {
		const contacts = visibleResults.filter((u) => u.source === "contact");
		const onPing = visibleResults.filter((u) => u.source !== "contact");
		const out: Array<{ title: string; users: readonly NewChatModalDirectoryUser[] }> = [];
		if (contacts.length > 0) out.push({ title: "YOUR CONTACTS", users: contacts });
		if (onPing.length > 0) out.push({ title: "ON PING", users: onPing });
		return out;
	}, [visibleResults]);

	// Debounced search. 450ms is enough to ride out a fast typist
	// (avg ~150ms between keystrokes for a phone number) without
	// firing a request per keystroke — the BE rate-limits
	// /v1/users/lookup at ~10rps per IP, and a 200ms gate let
	// rapid typeahead hit 429 mid-input. The AbortController kills
	// stale requests so a slow response can't land after a newer
	// query starts.
	useEffect(() => {
		if (!open) return;
		const controller = new AbortController();
		abortRef.current?.abort();
		abortRef.current = controller;
		const t = setTimeout(async () => {
			setIsLoading(true);
			setError(null);
			try {
				const users = await onSearch(query, controller.signal);
				if (!controller.signal.aborted) {
					setResults(users);
				}
			} catch (err) {
				if (controller.signal.aborted) return;
				const status = (err as { status?: number } | null)?.status;
				const serverMessage = (err as { serverMessage?: string } | null)
					?.serverMessage;
				if (status === 429) {
					setError(
						serverMessage ??
							"You're searching too fast — wait a moment and try again.",
					);
				} else {
					setError(
						serverMessage ?? "Couldn't search the directory. Try again.",
					);
				}
			} finally {
				if (!controller.signal.aborted) setIsLoading(false);
			}
		}, 450);
		return () => {
			controller.abort();
			clearTimeout(t);
		};
	}, [query, open, onSearch]);

	// Reset on close so the next open starts fresh
	useEffect(() => {
		if (!open) {
			setQuery("");
			setResults([]);
			setError(null);
			setRequestingId(null);
		}
	}, [open]);

	const handlePick = async (
		user: NewChatModalDirectoryUser,
	): Promise<void> => {
		setRequestingId(user.id);
		setError(null);
		try {
			const conversationId = await onPick(user);
			onConversationStarted(conversationId);
			onDismiss();
		} catch (err) {
			// Prefer the BE's human-readable message when present —
			// e.g. 410 `key_package_pool_empty` carries the string
			// "That account isn't ready to receive a chat right now",
			// which is more useful than the generic fallback.
			const serverMessage = (err as { serverMessage?: string } | null)
				?.serverMessage;
			setError(
				serverMessage && serverMessage.length > 0
					? serverMessage
					: "Couldn't start that chat. Try again.",
			);
			setRequestingId(null);
		}
	};

	if (!open) return null;

	const trimmed = query.trim();

	return (
		<Modal visible={open} transparent animationType="none" onRequestClose={onDismiss}>
			<Pressable
				accessibilityRole="none"
				onPress={onDismiss}
				style={StyleSheet.absoluteFill}
				className="z-50 flex-1 items-center justify-center px-md bg-black/60"
			>
				{/* Card — Figma 4036:14246: 500px, r12, #15171C, hairline border. */}
				<Pressable
					onPress={() => undefined}
					className="
						w-full max-w-[500px] max-h-[80vh] flex flex-col
						rounded-[12px] border
						bg-light-surface-primary border-light-border-secondary
						dark:bg-[#15171C] dark:border-[#22242A]
						shadow-2xl
					"
				>
					{/* Header */}
					<View className="flex flex-row items-center justify-between px-[29px] pt-5 pb-2">
						<Text className="m-0 font-sans font-bold text-[20px] leading-7 text-light-text-primary dark:text-white">
							New message
						</Text>
						<Pressable
							accessibilityRole="button"
							accessibilityLabel="Close"
							onPress={onDismiss}
							className="w-[26px] h-[26px] rounded-[8px] flex items-center justify-center hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]"
						>
							<X size={18} strokeWidth={2} color="#B5BAC3" />
						</Pressable>
					</View>

					<View className="px-[29px] pb-2">
						{/* Search field — 32px, r8, #111318. */}
						<View className="flex flex-row items-center h-8 px-sm gap-sm rounded-[8px] bg-light-surface-secondary dark:bg-[#111318]">
							<Search size={16} strokeWidth={2} color="#6B7280" />
							<TextInput
								value={query}
								onChangeText={setQuery}
								placeholder="Search by name, email or phone…"
								placeholderTextColor="#6B7280"
								autoCapitalize="none"
								autoCorrect={false}
								autoFocus
								className="flex-1 font-sans text-[12px] text-light-text-primary dark:text-white"
								style={WEB_INPUT_OUTLINE_RESET}
							/>
							{query.length > 0 ? (
								<Pressable
									accessibilityRole="button"
									accessibilityLabel="Clear search"
									onPress={() => setQuery("")}
									className="w-4 h-4 rounded-[4px] items-center justify-center"
								>
									<X size={14} strokeWidth={2} color="#6B7280" />
								</Pressable>
							) : null}
						</View>
					</View>

					<ScrollView
						accessibilityLiveRegion="polite"
						className="flex-1 min-h-0 px-[29px] pb-4"
					>
						{/* Share-your-link (empty-query state). */}
						{trimmed.length === 0 && shareLink !== undefined ? (
							<View className="pt-2 pb-3 gap-2">
								<Text className="font-sans text-[12px] font-bold leading-[17px] text-[#6B7280]">
									Share your link
								</Text>
								<View className="flex flex-row items-center h-[34px] px-3 gap-sm rounded-[12px] bg-light-surface-elevated dark:bg-[#22242A]">
									<Text
										numberOfLines={1}
										className="flex-1 font-sans text-[12px] leading-[17px] text-light-text-primary dark:text-[#E8EAED]"
									>
										{shareLink}
									</Text>
									<Pressable
										accessibilityRole="button"
										accessibilityLabel="Copy link"
										onPress={() => {
											try {
												if (typeof navigator !== "undefined" && navigator.clipboard) {
													void navigator.clipboard.writeText(shareLink);
													setCopied(true);
													setTimeout(() => setCopied(false), 1500);
												}
											} catch {
												/* clipboard unavailable */
											}
										}}
										className="w-[18px] h-[18px] items-center justify-center"
									>
										<Copy size={16} strokeWidth={2} color={copied ? "#537EFF" : "#B5BAC3"} />
									</Pressable>
								</View>
							</View>
						) : null}

						{error !== null && (
							<Text className="font-sans text-[12px] text-light-accent-rose px-sm py-sm m-0">
								{error}
							</Text>
						)}

						{isLoading && visibleResults.length === 0 ? (
							<SkeletonRows />
						) : visibleResults.length === 0 && !isLoading ? (
							<Text className="font-sans text-[12px] text-light-text-secondary dark:text-white/55 text-center px-sm py-xl m-0">
								{trimmed.length === 0
									? "Search for people by name, email or phone number."
									: "No one matches that search."}
							</Text>
						) : (
							<View className="flex flex-col gap-3">
								{sections.map((section) => (
									<View key={section.title} className="flex flex-col gap-1">
										<Text className="font-sans text-[12px] font-bold leading-[17px] text-[#6B7280] px-1">
											{section.title}
										</Text>
										<View className="flex flex-col gap-[2px]">
											{section.users.map((u) => (
												<ContactRow
													key={u.id}
													user={u}
													busy={requestingId === u.id}
													disabled={requestingId !== null}
													onPress={() => void handlePick(u)}
												/>
											))}
										</View>
									</View>
								))}
							</View>
						)}
					</ScrollView>
				</Pressable>
			</Pressable>
		</Modal>
	);
}

// Contact row (Figma `contact-list-item`, 45px, r8): 28px rounded avatar +
// name (14/700) + verify seal + role (12/400). Tapping starts the chat.
function ContactRow({
	user,
	busy,
	disabled,
	onPress,
}: {
	user: NewChatModalDirectoryUser;
	busy: boolean;
	disabled: boolean;
	onPress: () => void;
}): JSX.Element {
	return (
		<Pressable
			accessibilityRole="button"
			accessibilityLabel={`Message ${user.displayName}`}
			disabled={disabled}
			onPress={onPress}
			className="w-full flex flex-row items-center gap-sm h-[45px] px-1 rounded-[8px] hover:bg-light-surface-tertiary dark:hover:bg-white/[0.04] disabled:opacity-50"
		>
			<Avatar
				initials={user.displayName.charAt(0).toUpperCase() || "?"}
				{...(user.avatarUri !== undefined ? { imageUri: user.avatarUri } : {})}
				size="company"
				shape="rounded"
			/>
			<View className="flex-1 min-w-0 flex flex-col">
				<View className="flex flex-row items-center gap-1">
					<Text
						numberOfLines={1}
						className="font-sans text-[14px] leading-5 font-bold text-light-text-primary dark:text-[#F5F6F8]"
					>
						{user.displayName}
					</Text>
					{user.identityVerified === true ? <VerifiedBadge verified size={14} /> : null}
				</View>
				{(() => {
					const sub = [user.role, user.company].filter(Boolean).join(" · ");
					return sub.length > 0 ? (
						<Text
							numberOfLines={1}
							className="font-sans text-[12px] leading-[17px] text-light-text-secondary dark:text-[#B5BAC3]"
						>
							{sub}
						</Text>
					) : null;
				})()}
			</View>
			{busy ? (
				<Text className="font-sans text-[12px] text-light-text-tertiary dark:text-white/55">
					Starting…
				</Text>
			) : null}
		</Pressable>
	);
}

function SkeletonRows(): JSX.Element {
	return (
		<View className="flex flex-col gap-[2px]">
			{Array.from({ length: 5 }).map((_, i) => (
				<View
					// biome-ignore lint/suspicious/noArrayIndexKey: skeleton index is fine
					key={i}
					className="flex flex-row items-center gap-md px-md py-sm"
				>
					<View className="w-10 h-10 rounded-full bg-light-surface-tertiary dark:bg-white/[0.06]" />
					<View className="flex-1 flex flex-col gap-xs">
						<View className="h-3 w-2/5 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
						<View className="h-3 w-3/5 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
					</View>
				</View>
			))}
		</View>
	);
}
